/**
 * Radio search grid — Location column display logic.
 *
 * Derives the user-facing Location label + destination link from a radio's
 * current RadioLocation, for whichever placement level it populates. Pure
 * and route-config-free so RadioGrid's render layer and the support-view
 * RadioLocation display can both build on it without duplicating the link
 * contract. Links always come from the canonical `src/router/paths.js`
 * builders, never a hand-built template string.
 *
 * Contains:
 * - PLACEMENT_* — the placement enum (wire contract with pliny)
 * - deriveLocationDisplay — location + placement -> { label, link }
 */

import { refLabel } from '@sophocles/features/entities/references';
import { customerInventoryPath, siteGroupInventoryPath, sitePath } from '@sophocles/router/paths';

/** Placement enum — must match pliny/odl_app/serializers/radio.py + views/radios.py. */
export const PLACEMENT_ON_SITE = 'onSite';
export const PLACEMENT_NOT_INSTALLED = 'notInstalled';
export const PLACEMENT_CUSTOMER_INVENTORY = 'customerInventory';
export const PLACEMENT_PLANT_INVENTORY = 'plantInventory';
export const PLACEMENT_INVENTORY_LOCATION = 'inventoryLocation';

/**
 * Mirrors InventoryDashboard.normalizeRows: derive the user-facing
 * Location label + destination link from whichever placement level the
 * radio's current RadioLocation populates. The label matches the
 * server-derived `site_name` except for group placements, where the FE
 * composes the "name / description" ref label client-side (refLabel);
 * the link is FE routing and owned here.
 *
 * @param {Object|null} location - RadioLocation payload (radio.location)
 * @param {string} placementType - one of the PLACEMENT_* values above
 * @returns {{ label: string|null, link: string|null }}
 */
export function deriveLocationDisplay(location, placementType) {
  if (!location) return { label: null, link: null };

  if (placementType === PLACEMENT_ON_SITE && location.site) {
    const customerId = location.customer?.id;
    const groupId = location.site_group?.id;
    const siteId = location.site.id;
    const link =
      customerId && groupId && siteId
        ? sitePath({ cust_id: customerId, sitegroup_id: groupId, site_id: siteId })
        : null;
    return { label: location.site.name ?? '', link };
  }
  if (placementType === PLACEMENT_PLANT_INVENTORY && location.site_group) {
    const customerId = location.customer?.id;
    const groupId = location.site_group.id;
    const link = customerId && groupId ? siteGroupInventoryPath({ customerId, siteGroupId: groupId }) : null;
    return { label: refLabel(location.site_group), link };
  }
  if (placementType === PLACEMENT_CUSTOMER_INVENTORY && location.customer) {
    const customerId = location.customer.id;
    return {
      label: location.customer.name ?? '',
      link: customerId ? customerInventoryPath({ customerId }) : null,
    };
  }
  if (placementType === PLACEMENT_INVENTORY_LOCATION && location.inventory_location) {
    return { label: location.inventory_location.name ?? '', link: null };
  }
  return { label: null, link: null };
}
